import gatewayService from '../../../services/GatewayService';
import * as CONSTANTS from '../../constants';
import tokenStorageService from '../../../services/TokenStorageService';
import * as Converter from '../../../services/ConverterService';
import router from '../../../routes';
import * as Validator from '../../../services/ValidatorService';
import {
    calculatePeriodFromTo,
    addDay,
    calculateCalendarDates,
    getSeasonPeriod
} from '../../../services/DateToolService'
import {eventTracker} from '../../../services/GoogleAnalyticsService';

/**
 * Fetch all data needed for the manage allocation page.
 *
 * @param commit
 * @param payload
 * @returns {Promise<T>}
 */
export const fetchManageAllocationData = ({ dispatch }, payload) => {
    dispatch('getRequestList', payload);
    dispatch('getArrivalRules', payload);
    dispatch('getAllocationList', payload);
    dispatch('getAllocationListMerged', payload);
    dispatch('getAllocationCalendar', payload);
};

/**
 * Get all the data needed when handling a request.
 *
 * @param commit
 * @returns {Promise<[(T|never), (T|never), (T|never), (T|never)]>}
 */
export const fetchRequestUpdateData = ({ dispatch }) => {
    dispatch('getAllocationList');
    dispatch('getAllocationListMerged');
    dispatch('getAllocationCalendar');
    dispatch('getRequestList');
};

/**
 * Fetch all allocation data needed for allocation list and calendar.
 *
 * @param dispatch
 */
export const fetchAllocationData = ({ dispatch }) => {
    dispatch('getAllocationList');
    dispatch('getAllocationListMerged');
    dispatch('getAllocationCalendar');
};

/**
 * Retrieves list of allocations
 *
 * @param context
 * @param payload
 */
export const getAccommodationList = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_LIST_FETCH_PENDING);
    const token = tokenStorageService.get();
    return gatewayService.getAccommodationList(payload, token).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.ALLOCATION_LIST_FETCH_SUCCESS, res.data.data);
            commit(CONSTANTS.ALLOCATION_LIST_SELECTED_RESET);
        } else {
            commit(CONSTANTS.ALLOCATION_LIST_FETCH_NO_CONTENT);
            commit(CONSTANTS.ALLOCATION_LIST_SELECTED_RESET);
        }
    }).catch(() => {
        commit(CONSTANTS.ALLOCATION_LIST_FETCH_FAILURE);
        commit(CONSTANTS.ALLOCATION_LIST_SELECTED_RESET);
    });
};

/**
 * Get allocation list by accommodationSiteId.
 *
 * @param commit
 * @param state
 * @param payload
 * @returns {Promise<T | never>}
 */
export const getAllocationList = ({ commit, state }, payload) => {
    const token = tokenStorageService.get();
    const period = getSeasonPeriod(state.allocations.years.current);
    const accommodationSiteId = payload || parseInt(router.history.current.params.accommodationSiteId);

    commit(CONSTANTS.ALLOCATION_ALLOCATIONS_FETCH_PENDING);

    return gatewayService.getAllocationList(
        [accommodationSiteId],
        period.from,
        period.to,
        token
    ).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.ALLOCATION_ALLOCATIONS_FETCH_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.ALLOCATION_ALLOCATIONS_FETCH_NO_CONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.ALLOCATION_ALLOCATIONS_FETCH_FAILURE);
    })
};

/**
 * Get allocation list merged by accommodation site id.
 *
 * @param commit
 * @param state
 * @param payload
 * @returns {Promise<T | never>}
 */
export const getAllocationListMerged = ({ commit, state }, payload) => {
    const token = tokenStorageService.get();
    const period = getSeasonPeriod(state.allocations.years.current);
    const accommodationSiteId = payload || parseInt(router.history.current.params.accommodationSiteId);

    commit(CONSTANTS.ALLOCATION_ALLOCATIONS_MERGED_FETCH_PENDING);

    return gatewayService.getAllocationListMerged(
        [accommodationSiteId],
        period.from,
        period.to,
        token
    ).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.ALLOCATION_ALLOCATIONS_MERGED_FETCH_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.ALLOCATION_ALLOCATIONS_MERGED_FETCH_NO_CONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.ALLOCATION_ALLOCATIONS_MERGED_FETCH_FAILURE);
    })
};

/**
 * Get allocation calendar.
 *
 * @param commit
 * @param state
 * @param payload
 * @returns {Promise<T | never>}
 */
export const getAllocationCalendar = ({ commit, state }, payload) => {
    const token = tokenStorageService.get();
    const period = getSeasonPeriod(state.allocations.years.current);
    const accommodationSiteId = payload || parseInt(router.history.current.params.accommodationSiteId);

    commit(CONSTANTS.ALLOCATION_CALENDAR_FETCH_PENDING);

    return gatewayService.getAllocationCalendar(
        [accommodationSiteId],
        period.from,
        period.to,
        token
    ).then((res) => {
        commit(CONSTANTS.ALLOCATION_CALENDAR_FETCH_SUCCESS, res.data.data);
    }).catch(() => {
        commit(CONSTANTS.ALLOCATION_ALLOCATIONS_MERGED_FETCH_FAILURE);
    })
};

/**
 * Get request list.
 *
 * @param commit
 * @param state
 * @param payload
 * @returns {Promise<T | never>}
 */
export const getRequestList = ({ commit, state }, payload) => {
    const token = tokenStorageService.get();
    const period = getSeasonPeriod(state.allocations.years.current);
    const accommodationSiteId = payload || parseInt(router.history.current.params.accommodationSiteId);

    commit(CONSTANTS.ALLOCATION_REQUEST_LIST_PENDING);

    return gatewayService.getRequestList(
        token,
        [accommodationSiteId],
        period.from,
        period.to
    ).then((res) => {
        commit(CONSTANTS.ALLOCATION_REQUEST_LIST_SUCCESS, res.data.data);
    }).catch(() => {
        commit(CONSTANTS.ALLOCATION_REQUEST_LIST_FAILURE);
    })
};

/**
 * Get arrival rules.
 *
 * @param commit
 * @param state
 * @param payload
 * @returns {Promise<T | never>}
 */
export const getArrivalRules = ({ commit, state }, payload) => {
    const token = tokenStorageService.get();
    const period = getSeasonPeriod(state.allocations.years.current);
    const accommodationSiteId = payload || parseInt(router.history.current.params.accommodationSiteId);

    commit(CONSTANTS.ALLOCATION_ARRIVAL_LIST_PENDING);

    return gatewayService.getArrivalRules(
        token,
        [accommodationSiteId],
        period.from,
        period.to
    ).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.ALLOCATION_ARRIVAL_LIST_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.ALLOCATION_ARRIVAL_LIST_NO_CONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.ALLOCATION_ARRIVAL_LIST_FAILURE);
    })
};

/**
 * Sets the selected filters
 *
 * @param context
 * @param payload
 */
export const setSelectedFilters = ({ commit, state }, payload) => {
    if (payload.hasOwnProperty('site')) {
        payload.accommodation = undefined;
    }
    commit(CONSTANTS.ALLOCATION_LIST_SELECTED_SET, payload);
    // Set the page to the first one
    commit(CONSTANTS.ALLOCATION_LIST_PAGE_SET, 1);

    if (payload.hasOwnProperty('supplier')) {
        state.filters.selected.site = undefined;
        getAccommodationList({commit, state});
    }
};

/**
 * Sets the current page
 *
 * @param context
 * @param payload
 */
export const setPage = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_LIST_PAGE_SET, payload);
};

/**
 * Increments the current page
 *
 * @param context
 */
export const incrementPage = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_LIST_PAGE_INCREMENT);
};

/**
 * Decrements the current page
 *
 * @param context
 */
export const decrementPage = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_LIST_PAGE_DECREMENT);
};

/**
 * Sets the predicate to order by
 *
 * @param context
 * @param payload
 */
export const setPredicate = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_LIST_PREDICATE_SET, payload);
    commit(CONSTANTS.ALLOCATION_LIST_PAGE_SET, 1);
};

/**
 * Sets the limit per page
 *
 * @param context
 * @param payload
 */
export const setLimit = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_LIST_LIMIT_SET, payload);
    commit(CONSTANTS.ALLOCATION_LIST_PAGE_SET, 1);
};

/**
 * Sets the order parameter
 *
 * @param context
 * @param payload
 */
export const setOrder = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_LIST_ORDER_SET, payload);
    commit(CONSTANTS.ALLOCATION_LIST_PAGE_SET, 1);
};

/**
 * Sets a row in the allocation form
 *
 * @param commit
 * @param payload
 */
export const setRow = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_FORM_ROW_SET, payload);
};

/**
 * Adds a row to the allocation form
 *
 * @param commit
 * @param payload
 */
export const addRow = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_FORM_ROW_ADD, payload);
};

/**
 * Removes a row from the allocation form
 *
 * @param commit
 * @param payload
 */
export const removeRow = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_FORM_ROW_REMOVE, payload);
};

/**
 * Removes a row from the allocation form
 *
 * @param commit
 * @param payload
 */
export const duplicateRow = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_FORM_ROW_DUPLICATE, payload);
};

/**
 * Resets the form.
 *
 * @param commit
 */
export const resetForm = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_FORM_ROW_RESET);
};

/**
 * Resets the modal form.
 *
 * @param commit
 */
export const resetModalForm = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_MODAL_FORM_RESET);
};

/**
 * Submit the form.
 *
 * @param commit
 * @param payload
 */
export const submitForm = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_FORM_SUBMIT, payload);
};

/**
 * Cancel the form submission.
 *
 * @param commit
 */
export const cancelForm = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_FORM_CANCEL);
};

/**
 * Set the accommodation.
 *
 * @param commit
 * @param payload
 */
export const setAccommodation = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_SELECT, payload);
};

/**
 * Increment the page of the allocations list.
 *
 * @param commit
 */
export const incrementAllocationPagination = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_ALLOCATIONS_PAGE_INCREMENT);
};

/**
 * Decrement the page of the allocations list.
 *
 * @param commit
 */
export const decrementAllocationPagination = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_ALLOCATIONS_PAGE_DECREMENT);
};

/**
 * Set allocation page limit.
 *
 * @param commit
 * @param payload
 */
export const setAllocationLimit = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_ALLOCATIONS_LIMIT_SET, payload);
    commit(CONSTANTS.ALLOCATION_ALLOCATIONS_PAGE_SET, 1);
};

/**
 * Increment the page of the allocations list.
 *
 * @param commit
 */
export const incrementModalPagination = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_MODAL_PAGE_INCREMENT);
};

/**
 * Decrement the page of the allocations list.
 *
 * @param commit
 */
export const decrementModalPagination = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_MODAL_PAGE_DECREMENT);
};

/**
 * Set the page number for the modal pagination.
 *
 * @param commit
 * @param payload
 */
export const setModalPagination = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_MODAL_PAGE_SET, payload);
};

/**
 * Get filters for the manage allocations form
 *
 * @param context
 * @param payload
 */
export const getRouteFilters = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_ROUTE_FILTERS_PENDING);
    const token = tokenStorageService.get();
    return gatewayService.getAllocationFilterData(payload, token).then((res) => {
        commit(CONSTANTS.ALLOCATION_ROUTE_FILTERS_SUCCESS, res.data.data)
    }).catch(() => {
        commit(CONSTANTS.ALLOCATION_ROUTE_FILTERS_FAILURE);
    });
};

/**
 * Set the selected route filters
 *
 * @param commit
 * @param payload
 */
export const setSelectedRouteFilters = ({ commit }, payload) => {
    if (payload.hasOwnProperty('site') && !payload.hasOwnProperty('accommodation')) {
        payload.accommodation = undefined;
    }
    commit(CONSTANTS.ALLOCATION_ROUTE_FILTERS_SELECTED_SET, payload);
};

/**
 * Validate the allocation inputs.
 *
 * @param commit
 * @param state
 */
export const validateAddAllocationInputs = ({ commit, state }) => {
    const _rows = state.form.rows;
    const _validator = Validator.validateAddAllocationInputs(_rows);
    if (_validator.valid) {
        commit(CONSTANTS.ALLOCATION_FORM_VALIDATOR_SUCCESS);
    } else {
        commit(CONSTANTS.ALLOCATION_FORM_VALIDATOR_FAILURE, _validator.messages);
    }
};

/**
 * Validate the allocation inputs for updating allocation.
 *
 * @param commit
 * @param state
 */
export const validateUpdateAllocationInputs = ({ commit, state }) => {
    const _rows = state.modal.overlay.allocation;
    const _validator = Validator.validateAddAllocationInputs(_rows);
    if (_validator.valid) {
        commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_VALIDATOR_SUCCESS);
    } else {
        commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_VALIDATOR_FAILURE, _validator.messages);
    }
};

/**
 * Adds the allocations from the form and then reloads the page to fetch the new data.
 *
 * @param commit
 * @param state
 * @param dispatch
 * @param rootState
 */
export const addAllocations = ({ commit, state, dispatch, rootState }) => {
    commit(CONSTANTS.ALLOCATION_FORM_ACCEPT);
    commit(CONSTANTS.ALLOCATION_ALLOCATIONS_ADD_PENDING);
    const accommodationSiteId = state.form.filters.selected.accommodation.accommodationSiteId;
    const allocation = Converter.addAllocationData(accommodationSiteId, state.form.rows[0]);
    const token = tokenStorageService.get();
    const user = rootState.auth.user;

    return gatewayService.addAllocations(accommodationSiteId, allocation, token).then(() => {
        commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_SUCCESS, {
            text: 'manageAllocationModal.success',
            level: 'success'
        });
        commit(CONSTANTS.ALLOCATION_ALLOCATIONS_ADD_SUCCESS, {
            // Google Analytics
            meta: eventTracker('allocation', 'add', 'added allocation', user.id)
        });
        // commit(CONSTANTS.ALLOCATION_MODAL_TOGGLE, false);
        commit(CONSTANTS.ALLOCATION_MODAL_ADD_TOGGLE, false);
        dispatch('fetchAllocationData');
    }).catch((err) => {
        if (err.status === 403) {
            commit(CONSTANTS.ALLOCATION_ALLOCATIONS_ADD_FAILURE, {
                level: 'warning',
                text: 'allocationList.messages.unauthorized'
            });
        } else {
            commit(CONSTANTS.ALLOCATION_ALLOCATIONS_ADD_FAILURE, {
                level: 'error',
                text: 'allocationList.messages.error'
            });
        }
    });
};

/**
 * Update allocations from the modal overlay page.
 *
 * @param commit
 * @param state
 * @param dispatch
 * @param rootState
 * @returns {Promise<T>}
 */
export const updateAllocation = ({ commit, state, dispatch, rootState }) => {
    commit(CONSTANTS.ALLOCATION_FORM_ACCEPT);
    commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_PENDING);
    const accommodationSiteId = state.form.filters.selected.accommodation.accommodationSiteId;
    const allocation = Converter.updateAllocationData(
        state.modal.overlay.allocation.item,
        state.modal.overlay.allocation.original
    );
    const token = tokenStorageService.get();
    const user = rootState.auth.user;

    return gatewayService.updateAllocation(accommodationSiteId, allocation, token).then(() => {
        commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_SUCCESS, {
            text: 'manageAllocationModal.update',
            level: 'success',
            // Google Analytics
            meta: eventTracker('allocation', 'update', 'updated allocation', user.id)
        });
        commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE, false);
        commit(CONSTANTS.ALLOCATION_COMMENT_RESET);
        dispatch('fetchAllocationData');
    }).catch((err) => {
        if (err.status === 403) {
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_FAILURE, {
                level: 'warning',
                text: 'manageAllocationModal.unauthorized'
            });
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE, false);
        } else {
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_FAILURE, {
                level: 'error',
                text: 'manageAllocationModal.error'
            });
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE, false);
        }
    });
};

/**
 * Request an update for an allocation.
 *
 * @param commit
 * @param state
 * @param dispatch
 * @param rootState
 */
export const requestUpdateAllocation = ({ commit, state, dispatch, rootState }) => {
    commit(CONSTANTS.ALLOCATION_FORM_ACCEPT);
    commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_PENDING);
    const accommodationSiteId = state.form.filters.selected.accommodation.accommodationSiteId;
    const allocation = Converter.requestAllocationData(
        state.modal.overlay.allocation.item,
        state.modal.overlay.allocation.original
    );
    const token = tokenStorageService.get();
    const user = rootState.auth.user;

    return gatewayService.requestUpdateAllocation(accommodationSiteId, allocation, token).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_SUCCESS, {
                text: 'manageAllocationModal.request',
                level: 'success',
                // Google Analytics
                meta: eventTracker('allocation', 'request', 'requested change', user.id)
            });
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE, false);
            dispatch('fetchRequestUpdateData');
        }
    }).catch((err) => {
        if (err.status === 403) {
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_FAILURE, {
                level: 'warning',
                text: 'manageAllocationModal.unauthorized'
            });
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE, false);
        } else {
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_FAILURE, {
                level: 'error',
                text: 'manageAllocationModal.error'
            });
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE, false);
        }
    });
};

/**
 * Block allocations.
 *
 * @param commit
 * @param state
 * @param dispatch
 * @param rootState
 * @param payload
 */
export const blockAllocation = ({ commit, state, dispatch, rootState }, payload) => {
    commit(CONSTANTS.ALLOCATION_FORM_ACCEPT);
    commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_PENDING);
    const accommodationSiteId = state.form.filters.selected.accommodation.accommodationSiteId;
    const comment = state.modal.overlay.allocation.item.comment;
    const period = Converter.blockAllocationData(payload, comment);
    const token = tokenStorageService.get();
    const user = rootState.auth.user;

    return gatewayService.blockAllocation(accommodationSiteId, period, token).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_SUCCESS, {
                text: 'manageAllocationModal.blocked',
                level: 'success',
                // Google Analytics
                meta: eventTracker('allocation', 'block', 'blocked allocation', user.id)
            });
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE, false);
            commit(CONSTANTS.ALLOCATION_COMMENT_RESET);
            dispatch('fetchAllocationData');
        }
    }).catch((err) => {
        if (err.status === 403) {
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_FAILURE, {
                level: 'warning',
                text: 'allocationList.messages.unauthorized'
            });
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE, false);
        } else {
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_FAILURE, {
                level: 'error',
                text: 'allocationList.messages.error'
            });
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE, false);
        }
    });
};

/**
 * Request block allocation.
 *
 * @param commit
 * @param state
 * @param dispatch
 * @param rootState
 * @param payload
 * @returns {*|Promise<T>}
 */
export const requestBlockAllocation = ({ commit, state, dispatch, rootState }, payload) => {
    commit(CONSTANTS.ALLOCATION_FORM_ACCEPT);
    commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_PENDING);
    const accommodationSiteId = state.form.filters.selected.accommodation.accommodationSiteId;
    const period = Converter.blockAllocationData(payload);
    const token = tokenStorageService.get();
    const user = rootState.auth.user;

    return gatewayService.requestBlockAllocation(accommodationSiteId, period, token).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_SUCCESS, {
                text: 'manageAllocationModal.request',
                level: 'success',
                // Google Analytics
                meta: eventTracker('allocation', 'request', 'requested block', user.id)
            });
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE, false);
            dispatch('fetchRequestUpdateData');
        }
    }).catch((err) => {
        if (err.status === 403) {
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_FAILURE, {
                level: 'warning',
                text: 'allocationList.messages.unauthorized'
            });
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE, false);
        } else {
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_FAILURE, {
                level: 'error',
                text: 'allocationList.messages.error'
            });
            commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE, false);
        }
    });
};

/**
 * Reset the validation state.
 *
 * @param commit
 */
export const resetValidationMessage = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_FORM_VALIDATOR_RESET);
};

/**
 * Toggle the add allocation form.
 *
 * @param commit
 */
export const toggleAddAllocationForm = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_FORM_TOGGLE);
};

/**
 * Toggle modal and reset all functionality for modal.
 *
 * @param commit
 * @param state
 */
export const toggleModal = ({ commit, state }) => {
    if(state.calendar.from && state.calendar.to) {
        commit(CONSTANTS.ALLOCATION_MODAL_PAGE_SET, 1);
        commit(CONSTANTS.ALLOCATION_FORM_TOGGLE, false);
        commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE, false);
        commit(CONSTANTS.ALLOCATION_MODAL_ADD_TOGGLE, false);
        commit(CONSTANTS.ALLOCATION_MODAL_TOGGLE);
        commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_ERROR_MESSAGE_RESET);
    }
};

/**
 * Toggle the manage allocation modal.
 *
 * @param commit
 */
export const toggleModalOverlay = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE);
    commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_BOOKINGS_PAGE_SET, 1);
};

/**
 * Set the values for the manage allocations modal form.
 *
 * @param commit
 * @param payload
 */
export const setModalRow = ({ commit }, payload) => {
    // If changing release method then set other release method to undefined
    if(payload.releaseMethod === 'days') {
        payload = {...payload, releaseDate: undefined}
    } else if (payload.releaseMethod === 'date') {
        payload = {...payload, releaseDays: undefined}
    }
    commit(CONSTANTS.ALLOCATION_MODAL_FORM_SET, payload);
};

/**
 * Set the values for the manage allocations modal form.
 *
 * @param commit
 * @param payload
 */
export const setOverlayRow = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_FORM_SET, payload);
};

/**
 * Reset the allocation item.
 *
 * @param commit
 */
export const resetOverlayRow = ({commit}) => {
    commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_FORM_RESET)
};

/**
 * Sets the chosen allocation for the modal overlay.
 *
 * @param commit
 * @param state
 * @param payload
 */
export const chooseAllocation = ({commit, state}, payload) => {
    // Calculate the date to use for the allocation
    const period = calculatePeriodFromTo(state.calendar.from, state.calendar.to, payload);
    // Send a copy of the allocation object to be able to manipulate it
    commit(CONSTANTS.ALLOCATION_MODAL_ALLOCATION_SET, {...Converter.clone(payload), ...period});
    commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE, true);
    commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_BOOKINGS_PAGE_SET, 1);
};

/**
 * Increments the overlays bookings page
 *
 * @param context
 */
export const incrementOverlayBookingsPage = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_BOOKINGS_PAGE_INCREMENT);
};

/**
 * Decrements the overlays bookings page
 *
 * @param context
 */
export const decrementOverlayBookingsPage = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_MODAL_OVERLAY_BOOKINGS_PAGE_DECREMENT);
};

/**
 * Set the date for the allocation calendar.
 * If the from date is not set then that date will be set, else it will be the to date
 * and if both is set then the modal opens.
 *
 * @param commit
 * @param state
 * @param payload
 */
export const selectCalendarDate = ({ commit, state }, payload) => {
    if (!state.calendar.from) {
        commit(CONSTANTS.ALLOCATION_CALENDAR_DATE_SET, {data: {from: payload.day, startIndex: payload.index}});
    } else if (state.calendar.from && !state.calendar.to) {
        // Add one more day to the end date
        commit(CONSTANTS.ALLOCATION_CALENDAR_DATE_SET, {data: {to: addDay(payload.day)}});
        commit(CONSTANTS.ALLOCATION_MODAL_TOGGLE, true);
    }
};

/**
 * Deselect the date from the allocation calendar.
 *
 * @param commit
 * @param state
 */
export const deselectCalendarDate = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_CALENDAR_DATE_SET, {data: {from: undefined, startIndex: undefined}});
};

/**
 * Increment the page for the calendar pagination.
 *
 * @param commit
 * @param payload
 */
export const incrementCalendarPage = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_CALENDAR_PAGE_INCREMENT);
};

/**
 * Decrement the page for the calendar pagination.
 *
 * @param commit
 * @param payload
 */
export const decrementCalendarPage = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_CALENDAR_PAGE_DECREMENT);
};

/**
 * Chose calendar display option.
 *
 * @param commit
 * @param payload
 */
export const choseCalendarDisplay = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_CALENDAR_DISPLAY_SET, payload);
};

/**
 * Toggle the calendar infobox.
 *
 * @param commit
 * @param payload
 */
export const toggleCalendarInfoBox = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_CALENDAR_INFO_TOGGLE, payload);
};

/**
 * Change the info page number.
 *
 * @param commit
 * @param payload
 */
export const infoPageSet = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_CALENDAR_INFO_PAGE_SET, payload);
};

/**
 * Toggle modal add allocation form
 *
 * @param commit
 */
export const toggleModalAddAllocations = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_MODAL_ADD_TOGGLE)
};

/**
 * Add new allocation for period that is already assigned, so change dates for calendar and toggle modal add allocation
 *
 * @param commit
 * @param payload
 */
export const toggleModalNewAllocation = ({ commit }, payload) => {
    // Set the period to the full period of the allocation chosen
    commit(CONSTANTS.ALLOCATION_CALENDAR_DATE_SET, {from: payload.from, to: payload.to});
    // Toggle the ad allocation modal
    commit(CONSTANTS.ALLOCATION_MODAL_ADD_TOGGLE)
};

/**
 * Change the year.
 *
 * @param commit
 * @param state
 * @param payload
 */
export const updateYear = ({ commit, dispatch }, payload) => {
    commit(CONSTANTS.ALLOCATION_YEAR_SET, payload);
    // Fetch data and toggle the modal overlay
    dispatch('fetchAllocationData');
};

/**
 * Request accept action.
 *
 * @param commit
 * @param dispatch
 * @param rootState
 * @param payload
 * @returns {Promise<T>}
 */
export const requestAccept = ({ commit, dispatch, rootState }, payload) => {
    commit(CONSTANTS.ALLOCATION_REQUEST_UPDATE_PENDING);
    const token = tokenStorageService.get();
    const user = rootState.auth.user;

    return gatewayService.requestAccept(token, payload).then((res) => {
        if(res.status === 200) {
            commit(CONSTANTS.ALLOCATION_REQUEST_UPDATE_SUCCESS, {
                // Google Analytics
                meta: eventTracker('allocation', 'request', 'request accepted', user.id)
            });
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'request.accept.success',
                level: 'success'
            }, {root: true});
            dispatch('fetchRequestUpdateData');
        }
    }).catch(() => {
        commit(CONSTANTS.ALLOCATION_REQUEST_UPDATE_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'request.accept.failure',
            level: 'danger'
        }, {root: true});
    })
};

/**
 * Accept request with modifications.
 *
 * @param commit
 * @param dispatch
 * @param state
 * @param rootState
 * @returns {*|Promise<T>}
 */
export const requestAcceptModify = ({ commit, dispatch, state, rootState }) => {
    commit(CONSTANTS.ALLOCATION_REQUEST_UPDATE_PENDING);
    commit(CONSTANTS.ALLOCATION_REQUEST_EDIT_TOGGLE, false);
    const token = tokenStorageService.get();
    const item = state.request.item;
    const itemIndex = state.request.itemIndex;
    const original = state.request.items.pending[itemIndex];
    const allocationObject = Converter.modifyAllocationData(item, original);
    const user = rootState.auth.user;

    return gatewayService.requestAcceptModfied(token, {id: item.id, allocationObject: allocationObject}).then((res) => {
        if(res.status === 200) {
            commit(CONSTANTS.ALLOCATION_REQUEST_UPDATE_SUCCESS, {
                // Google Analytics
                meta: eventTracker('allocation', 'request', 'request accepted with modifications', user.id)
            });
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'request.modify.success',
                level: 'success'
            }, {root: true});
            commit(CONSTANTS.ALLOCATION_REQUEST_ITEM_DESELECT);
            dispatch('fetchRequestUpdateData');
        }
    }).catch(() => {
        commit(CONSTANTS.ALLOCATION_REQUEST_UPDATE_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'request.modify.failure',
            level: 'danger'
        }, {root: true});
    });
};

/**
 * Request deny action.
 *
 * @param commit
 * @param dispatch
 * @param rootState
 * @param payload
 * @returns {Promise<T>}
 */
export const requestDeny = ({ commit, dispatch, rootState }, payload) => {
    commit(CONSTANTS.ALLOCATION_REQUEST_UPDATE_PENDING);
    const token = tokenStorageService.get();
    const user = rootState.auth.user;

    return gatewayService.requestDeny(token, payload).then((res) => {
        if(res.status === 200) {
            commit(CONSTANTS.ALLOCATION_REQUEST_UPDATE_SUCCESS, {
                // Google Analytics
                meta: eventTracker('allocation', 'request', 'request denied', user.id)
            });
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'request.decline.success',
                level: 'success'
            }, {root: true});
            dispatch('fetchRequestUpdateData');
        }
    }).catch(() => {
        commit(CONSTANTS.ALLOCATION_REQUEST_UPDATE_FAILURE);
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'request.decline.failure',
            level: 'danger'
        }, {root: true});
    })
};

/**
 * Cancel a request.
 *
 * @param commit
 * @param dispatch
 * @param rootState
 * @param payload
 * @returns {Promise<T>}
 */
export const requestCancel = ({ commit, dispatch, rootState }, payload) => {
    commit(CONSTANTS.ALLOCATION_REQUEST_UPDATE_PENDING);
    const token = tokenStorageService.get();
    const user = rootState.auth.user;

    return gatewayService.requestCancel(token, payload).then(() => {
        commit(CONSTANTS.ALLOCATION_REQUEST_UPDATE_SUCCESS, {
            // Google Analytics
            meta: eventTracker('allocation', 'request', 'request cancelled', user.id)
        });
        commit('alert/ALERT_COUNTDOWN_RESET', {
            message: 'request.cancel.success',
            level: 'success'
        }, {root: true});
        dispatch('fetchRequestUpdateData');
    }).catch((err) => {
        commit(CONSTANTS.ALLOCATION_REQUEST_UPDATE_FAILURE);
        if(err.status === 403) {
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'request.cancel.unauth',
                level: 'danger'
            }, {root: true});
        } else {
            commit('alert/ALERT_COUNTDOWN_RESET', {
                message: 'request.cancel.failure',
                level: 'danger'
            }, {root: true});
        }
    })
};

/**
 * Increment the request page.
 *
 * @param commit
 */
export const requestPageIncrement = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_REQUEST_PAGE_INCREMENT);
};

/**
 * Decrement the request page.
 *
 * @param commit
 */
export const requestPageDecrement = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_REQUEST_PAGE_DECREMENT);
};

/**
 * Set the request predicate.
 *
 * @param commit
 * @param payload
 */
export const requestPredicateSet = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_REQUEST_PREDICATE_SET, payload);
    commit(CONSTANTS.ALLOCATION_REQUEST_PAGE_RESET);
};

/**
 * Select at specific request item.
 *
 * @param commit
 * @param payload
 */
export const requestItemSelect = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_REQUEST_ITEM_SELECT, {...payload, request: Converter.clone(payload.request)});
};

/**
 * Set the request item.
 *
 * @param commit
 * @param payload
 */
export const requestItemSet = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_REQUEST_ITEM_SET, payload);
};

/**
 * Toggle the request edit page.
 *
 * @param commit
 * @param payload
 */
export const requestEditToggle = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_REQUEST_EDIT_TOGGLE, payload);
    if(payload !== undefined && !payload) {
        commit(CONSTANTS.ALLOCATION_REQUEST_ITEM_DESELECT);
    }
};

/**
 * Allocation list option select.
 *
 * @param commit
 * @param payload
 */
export const allocationListOptionSelect = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_LIST_DISPLAY_SELECT, payload);
};

/**
 * Allocation list option reset.
 *
 * @param commit
 */
export const allocationListOptionReset = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_LIST_DISPLAY_RESET);
};

/**
 * Counts down the allocation timer.
 *
 * @param commit
 */
export const countdown = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_COUNTDOWN_DECREMENT);
};

/**
 * Retrieve the route filter data and set filter in state.
 *
 * @param commit
 * @param state
 * @param getters
 * @param payload
 */
export const setRouteFilters = ({ commit, state }, payload) => {
    const sites = state.form.filters.sites;
    const routeData = Converter.getRouteFilterData(sites, payload.siteId, payload.accommodationSiteId);
    commit(CONSTANTS.ALLOCATION_ROUTE_FILTERS_SELECTED_SET, routeData);
};

/**
 * Calculate and set the period for the calendar to which year and which months to display.
 * - At the 1st of november the calendar year will switch.
 *
 * @param commit
 */
export const setCalendarDates = ({commit}) => {
    const dateObject = calculateCalendarDates();
    commit(CONSTANTS.ALLOCATION_CALENDAR_DATES_SET, dateObject);
};

/**
 * Reset the allocations state.
 *
 * @param commit
 */
export const resetState = ({ commit }) => {
    commit(CONSTANTS.ALLOCATION_RESET_STATE);
};

/**
 * Add allocation comment.
 *
 * @param commit
 * @param payload
 */
export const addAllocationComment = ({ commit }, payload) => {
    commit(CONSTANTS.ALLOCATION_COMMENT_SET, payload);
};



// WEBPACK FOOTER //
// ./src/store/modules/allocation/actions.js